import React, { type PropsWithChildren } from 'react';
import Button, {
	IconButton,
	SplitButton,
	type ButtonProps,
	type IconButtonProps,
} from '@atlaskit/button/new';
import LinkIcon from '@atlaskit/icon/core/link';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import {
	ThemedButtonNew as ThemedButton,
	ThemedIconButton,
} from '@atlassian/jira-project-theme-components/src/ui/themed-button-new/ThemedButtonNew.tsx';
import type { CustomTriggerButtonProps, RenderCustomTriggerButton } from '../../common/types.tsx';
import { useCopyLink } from '../../common/hooks/use-copy-link.tsx';
import { messages } from './messages.tsx';

export const SplitTriggerButton: RenderCustomTriggerButton = (
	{
		isDisabled,
		isSelected,
		onClick: handleCustomTriggerClick,
		iconBefore,
	}: CustomTriggerButtonProps,
	triggerProps,
) => {
	const { formatMessage } = useIntl();
	const handleCopyLink = useCopyLink();

	return (
		<SplitButton appearance="default">
			<Tooltip content={formatMessage(messages.shareButtonLabel)} position="top">
				<Button
					iconBefore={iconBefore}
					isSelected={isSelected}
					isDisabled={isDisabled}
					onClick={handleCustomTriggerClick}
					{...triggerProps}
				>
					{formatMessage(messages.shareButtonLabel)}
				</Button>
			</Tooltip>
			<IconButton
				icon={LinkIcon}
				onClick={handleCopyLink}
				label={formatMessage(messages.copyLinkLabel)}
				isTooltipDisabled={false}
				tooltip={{ position: 'top' }}
			/>
		</SplitButton>
	);
};

export const SplitButtonTriggerWithTheming = ({
	primaryButtonOverrides,
	iconButtonOverrides,
}: {
	primaryButtonOverrides?: Partial<ButtonProps>;
	iconButtonOverrides?: Omit<Partial<IconButtonProps>, 'icon' | 'label' | 'onClick'>;
}) => {
	const { formatMessage } = useIntl();
	const handleCopyLink = useCopyLink();

	return (
		<SplitButton>
			<Tooltip content={formatMessage(messages.shareButtonLabel)} position="top">
				<ThemedButton {...primaryButtonOverrides}>
					{formatMessage(messages.shareButtonLabel)}
				</ThemedButton>
			</Tooltip>
			<ThemedIconButton
				icon={LinkIcon}
				onClick={handleCopyLink}
				label={formatMessage(messages.copyLinkLabel)}
				isTooltipDisabled={false}
				tooltip={{ position: 'top' }}
				{...iconButtonOverrides}
			/>
		</SplitButton>
	);
};

export const renderCustomSplitTriggerButtonWithTheming = ({
	primaryButtonOverrides,
	iconButtonOverrides,
}: {
	primaryButtonOverrides?: Omit<
		Partial<ButtonProps>,
		'iconBefore' | 'isSelected' | 'isDisabled' | 'onClick'
	>;
	iconButtonOverrides?: Omit<Partial<IconButtonProps>, 'icon' | 'label' | 'onClick'>;
}) => {
	const ThemedSplitTriggerButton: RenderCustomTriggerButton = (
		{
			isDisabled,
			isSelected,
			onClick: handleCustomTriggerClick,
			iconBefore,
		}: CustomTriggerButtonProps,
		triggerProps,
	) => {
		const { formatMessage } = useIntl();
		const handleCopyLink = useCopyLink();

		return (
			<SplitButton>
				<Tooltip content={formatMessage(messages.shareButtonLabel)} position="top">
					<ThemedButton
						{...triggerProps}
						iconBefore={iconBefore}
						isSelected={isSelected}
						isDisabled={isDisabled}
						onClick={handleCustomTriggerClick}
						{...primaryButtonOverrides}
					>
						{formatMessage(messages.shareButtonLabel)}
					</ThemedButton>
				</Tooltip>

				<ThemedIconButton
					icon={LinkIcon}
					onClick={handleCopyLink}
					label={formatMessage(messages.copyLinkLabel)}
					isTooltipDisabled={false}
					tooltip={{ position: 'top' }}
					{...iconButtonOverrides}
				/>
			</SplitButton>
		);
	};

	return ThemedSplitTriggerButton;
};

export const SplitButtonTriggerWithThemingWrapper = ({
	iconButtonOverrides,
	children,
}: PropsWithChildren<{
	iconButtonOverrides?: Omit<Partial<IconButtonProps>, 'icon' | 'label' | 'onClick'>;
}>) => {
	const { formatMessage } = useIntl();
	const handleCopyLink = useCopyLink();
	return (
		<SplitButton>
			<Tooltip content={formatMessage(messages.shareButtonLabel)} position="top">
				{children}
			</Tooltip>
			<ThemedIconButton
				icon={LinkIcon}
				onClick={handleCopyLink}
				label={formatMessage(messages.copyLinkLabel)}
				isTooltipDisabled={false}
				tooltip={{ position: 'top' }}
				{...iconButtonOverrides}
			/>
		</SplitButton>
	);
};
